import * as React from 'react'
import { PPDialog } from 'components/PPDialog'
import { FormattedMessage, useIntl } from 'react-intl'
import { FileFolder, FileType, MyFile } from 'interfaces'
import { PHOTO_ROTATION_TOGGLE, HALF_ROUND_DEGREES, DEFAULT_STUDIO_EXPORT_FILE_NAME } from 'utils/file'
import { ColorGradient } from 'utils/colors'
import { getMediaDimensions, getFileTypeByMimeType } from 'utils/file/helpers'

import styles from './MultiUploadManager.pcss'
import { FileUploadInfo } from 'services/uploads/state'
import { SnackType } from 'services/snackbar/interfaces/PendingTreat'
import UploadFilesPanel from './UploadFilesPanel'
import UploadTargetPanel from './UploadTargetPanel'

const MIN_FOLDER_NAME_LENGTH = 2
const BASE_TEN = 10

export interface MultiUploadManagerProps {
  folders: FileFolder[]
  files: { [name: string]: File }
  dialogOpen: boolean
  fileSizeLimits: { [key: string]: number }
  copyFile?: MyFile
  setFiles: (files: { [name: string]: File }) => void
  onCloseDialog: () => void
  createFolder: (
    name: string,
    color: string,
    isPrivate: boolean,
    featuredImage?: File,
    coverImages?: File[]
  ) => Promise<any>
  uploadFiles: (files: Array<FileUploadInfo>, folders: FileFolder[]) => void
  showNotification: (text: string, type?: SnackType, timeout?: number) => void
  clearCopyFile: () => void
  onCopyFile: (file: MyFile, folder: { id: string, name: string }) => void
  onCopyFileToNewFolder: (file: MyFile, folder: { name: string, color: string, isPrivate: boolean }) => void
}

export function MultiUploadManager(props: MultiUploadManagerProps) {
  const intl = useIntl()
  const {
    files,
    setFiles,
    folders,
    dialogOpen,
    fileSizeLimits,
    copyFile,
    onCloseDialog,
    createFolder: createFolderAction,
    uploadFiles: uploadFilesAction,
    showNotification,
    clearCopyFile,
    onCopyFile,
    onCopyFileToNewFolder
  } = props

  const [imagesSrcs, setImagesSrcs] = React.useState<Record<string, string>>({})
  const [filter, setFilter] = React.useState('')
  const [fileRotation, setFileRotation] = React.useState<{ [name: string]: string }>({})
  const [selectedFolders, setSelectedFolders] = React.useState<{ [id: string]: boolean }>({})
  const [mode, setMode] = React.useState<'create' | 'save'>('create')
  const [isCreatingFolder, setIsCreatingFolder] = React.useState(false)
  const newFolderName = React.useRef('')

  // Per-file validation errors live only in a ref: they are written on validation and
  // read once inside uploadFiles, so they never need to trigger a re-render.
  const fileErrorsRef = React.useRef<{ [name: string]: string }>({})

  React.useEffect(() => {
    // EXPL: Reset rotation of image added from Content Studio when dialog is closed.
    // This prevents consecutive added images to appear rotated by default.
    if (!dialogOpen && fileRotation[DEFAULT_STUDIO_EXPORT_FILE_NAME] !== '0') {
      setFileRotation(current => ({ ...current, [DEFAULT_STUDIO_EXPORT_FILE_NAME]: '0' }))
    }
  }, [fileRotation, dialogOpen])

  const onFileError = React.useCallback((fileName: string, error: string) => {
    fileErrorsRef.current = { ...fileErrorsRef.current, [fileName]: error }
  }, [])

  const onFolderClick = React.useCallback((folder: FileFolder) => {
    setSelectedFolders(prev => ({ ...prev, [folder.id]: !prev[folder.id] }))
  }, [])

  const clearAllFiles = React.useCallback(() => {
    fileErrorsRef.current = {}
    setFiles({})
    setImagesSrcs({})
  }, [setFiles])

  const close = React.useCallback(() => {
    onCloseDialog()
    clearAllFiles()
    setSelectedFolders({})
  }, [onCloseDialog, clearAllFiles])

  const toggleFileRotation = React.useCallback((fileName: string) => {
    setFileRotation(prevRotations => {
      const current = prevRotations[fileName] || 0
      return { ...prevRotations, [fileName]: PHOTO_ROTATION_TOGGLE[current].next }
    })
  }, [])

  const onFilterChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value)
  }, [])

  const removeFile = React.useCallback((fileName: string) => {
    const nextFiles = { ...files }
    delete nextFiles[fileName]
    setFiles(nextFiles)
    setImagesSrcs(current => {
      const next = { ...current }
      delete next[fileName]
      return next
    })
  }, [files, setFiles])

  const onFileLoaded = React.useCallback((name: string, src: string) => {
    setImagesSrcs(current => ({ ...current, [name]: src }))
  }, [])

  const addMoreFiles = React.useCallback((newFiles: { [name: string]: File }) => {
    setFiles({ ...files, ...newFiles })
  }, [files, setFiles])

  const uploadFiles = React.useCallback((folder?: { id: string, name: string }) => {
    const fileErrors = fileErrorsRef.current
    const validSelectedFileNames = Object.keys(files).filter(name => files[name] && !fileErrors[name])
    const validSelectedFiles = validSelectedFileNames.map(name => {
      const file = files[name]
      const rotateBy = parseInt(fileRotation[name] || '0', BASE_TEN)
      const withRotation = rotateBy % HALF_ROUND_DEGREES !== 0
      const type = getFileTypeByMimeType(file.type)
      const mediaElementType = type === FileType.Image || type === FileType.Gif
        ? 'image'
        : type === FileType.Video ? 'video' : undefined

      try {
        const sizes = getMediaDimensions(mediaElementType, `[data-id="${file.name}"]`, withRotation)
        return {
          file,
          width: sizes.width,
          height: sizes.height,
          rotateBy
        }
      } catch (e) {
        console.log('[getMediaDimensions()] Error: ', e)
        return undefined
      }
    })
      .filter(Boolean) as any[]

    const targetFolders = folder ? [folder as FileFolder] : Object.keys(selectedFolders)
      .map(id => Boolean(selectedFolders[id]) && folders.find(f => f.id.toString() === id))
      .filter(Boolean) as FileFolder[]

    uploadFilesAction(validSelectedFiles, targetFolders)
    close()
  }, [files, fileRotation, selectedFolders, folders, uploadFilesAction, close])

  const createNewFolder = React.useCallback((
    name: string,
    color: string,
    isPrivate?: boolean,
    featuredImage?: File,
    coverImages?: File[]
  ) => {
    setIsCreatingFolder(true)
    createFolderAction(name, color, Boolean(isPrivate), featuredImage, coverImages)
      .then(response => {
        const f = response?.folder
        if (f) {
          uploadFiles({ id: f.id, name: f.name })
          newFolderName.current = ''
        }
      })
      .catch((err) => {
        const msg = err.response?.message || err.message
        showNotification(
          intl.formatMessage({ id: 'notifications.error-w-message' }, { error: msg }) as string,
          'error',
          4000 // eslint-disable-line no-magic-numbers
        )
      })
      .finally(() => setIsCreatingFolder(false))
  }, [createFolderAction, showNotification, intl, uploadFiles])

  const startUpload = React.useCallback(() => {
    const myFile = copyFile
    if (myFile) {
      const targetFolders = Object.keys(selectedFolders).map(id => ({
        id,
        name: folders.find(f => f.id === id)?.name || ''
      }))

      targetFolders.forEach(f => onCopyFile(myFile, f))
      close()
      return
    }
    uploadFiles()
  }, [copyFile, selectedFolders, folders, onCopyFile, close, uploadFiles])

  const createFolder = React.useCallback((
    name: string,
    color: ColorGradient,
    isPrivate?: boolean | undefined,
    featuredImage?: string | File,
    coverImages?: string[]
  ) => {
    const folderName = name.trim()
    if (folderName.length < MIN_FOLDER_NAME_LENGTH) {
      return true
    }
    newFolderName.current = name
    let featuredImageFile = typeof featuredImage === 'string' ? files[featuredImage || ''] : undefined
    if (!featuredImageFile && typeof featuredImage === 'object') {
      featuredImageFile = featuredImage
    }

    if (copyFile) {
      onCopyFileToNewFolder(
        copyFile,
        { name: folderName, color: `${color.from},${color.to}`, isPrivate: Boolean(isPrivate) }
      )
      close()
      return true
    }

    const coverImagesFiles = coverImages ? coverImages.map(imageName => files[imageName]) : undefined
    createNewFolder(folderName, `${color.from},${color.to}`, isPrivate, featuredImageFile, coverImagesFiles)
    return true
  }, [files, copyFile, onCopyFileToNewFolder, close, createNewFolder])

  const images = React.useMemo(() => Object.values(imagesSrcs), [imagesSrcs])
  const filesCount = Object.keys(files).length

  return (
    <PPDialog
      data-testid="file-upload-popup"
      open={dialogOpen}
      onClose={close}
      className={styles.dialog}
      title={<FormattedMessage id={copyFile ? 'uploads.popup.header-copy' : 'uploads.popup.header-upload'} />}
    >
      <div className={styles.grid}>
        <UploadFilesPanel
          files={files}
          fileRotation={fileRotation}
          fileSizeLimits={fileSizeLimits}
          copyFile={copyFile}
          onClearCopyFile={clearCopyFile}
          onAddMoreFiles={addMoreFiles}
          onFileError={onFileError}
          onToggleRotation={toggleFileRotation}
          onRemoveFile={removeFile}
          onFileLoaded={onFileLoaded}
        />
        <UploadTargetPanel
          folders={folders}
          mode={mode}
          filter={filter}
          selectedFolders={selectedFolders}
          isCreatingFolder={isCreatingFolder}
          filesCount={filesCount}
          images={images}
          onModeChange={setMode}
          onFilterChange={onFilterChange}
          onFolderClick={onFolderClick}
          onClose={close}
          onStartUpload={startUpload}
          onCreateFolder={createFolder}
        />
      </div>
    </PPDialog>
  )
}

export default MultiUploadManager
